import { ClockCircleFilled, UserOutlined } from '@ant-design/icons';
import { ArrowStatefulIcon } from '@mm/icons';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { showSlideoutMenu } from '@mm/state/actions/viewActions';
import { connectedPageSelector } from '@mm/state/selectors/viewSelectors';
import { useActor } from '@xstate/react';
import classNames from 'classnames';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useContextSelector } from 'use-context-selector';
import Spinner from '../../../../../common/components/Spinner';
import { ContactDeleteModal } from '../../../contacts/components/ContactDeleteModal/ContactDeleteModal';
import ContactsDetailsHeader from '../../../contacts/components/contactDetails/contactDetailsHeader/contactDetailsHeader';
import ChatVariablesTab from '../../../contacts/components/contactDetails/contactDetailsTabs/chatVariablesTab/chatVariablesTab';
import { ChatVariablesTags } from '../../../contacts/components/contactDetails/contactDetailsTabs/chatVariablesTags/chatVariablesTags';
import { CustomerJourney } from '../../../contacts/components/contactDetails/contactDetailsTabs/customerJourney/customerJourney';
import { LeadScoring } from '../../../contacts/components/contactDetails/contactDetailsTabs/leadScoring/leadScoring';
import { ContactContext, EmptySelectedContact } from '../../contactMachine';
import { LiveChatContext } from '../../LiveChatContext';
import { BusinessAttributes } from './components/BusinessAttributes';
import { LinkedContacts } from './components/LinkedContacts';
import Notes from './components/Notes';
import { OTNTags } from './components/OTNTags';
import Sessions from './components/Sessions';
import './Contact.pcss';

type ContactProps = {
  collapsedContactView: boolean;
  setCollapsedContactView: (collapsed: boolean) => void;
};
export const Contact = ({
  collapsedContactView,
  setCollapsedContactView,
}: ContactProps) => {
  const { deleteSelectedContact } = useContactsCRUDActions();
  const connectedPage = useSelector(connectedPageSelector);

  const [deleteModalOpened, setDeleteModalOpened] = useState(false);

  const dispatch = useDispatch();

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineState, lcMachineSend] = useActor(lcService);

  const { remoteUserId } = lcMachineState.context || {};

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const selectedContactMachine = remoteUserId
    ? lcMachineState.context.contactsRefs[remoteUserId]
    : undefined;

  const refreshSelectedContact = useCallback(() => {
    selectedContactMachine?.send({
      type: 'LC_CONTACT_REFRESH',
      payload: remoteUserId || '',
    });
  }, [selectedContactMachine?.id, remoteUserId]);

  if (!selectedContact) return null;

  const displayVariables = selectedContact.variables.filter(
    v => v.variable.name !== 'fb messenger user id',
  );

  const isBusinessContact = !!displayVariables.find(
    ({ variable }) => variable.name.toLocaleLowerCase() === 'business_contact',
  );

  const className = classNames('live-chat--contact', {
    'live-chat--contact--collapsed': collapsedContactView,
  });
  const otnTags = selectedContact.tags.filter(tag => tag.kind === 'otn');

  const updateRemoteUserId = (id: string | undefined) => {
    lcMachineSend({ type: 'LC_REMOTE_USER_ID_CHANGE', payload: id });
  };

  const deleteContact = async (eraseRemoteMessages: boolean) => {
    if (connectedPage && selectedContact.id) {
      await deleteSelectedContact(
        connectedPage.id,
        selectedContact.id,
        undefined,
        undefined,
        undefined,
        eraseRemoteMessages,
      );
      if (selectedContact.conversation) {
        lcMachineSend({
          type: 'LC_CONVERSATIONS_DELETE',
          payload: [selectedContact.remoteId],
        });
      }
      updateRemoteUserId(undefined);
    }
  };
  const onHeaderButtonClick = () => {
    dispatch(showSlideoutMenu('attributesAndTags'));
  };

  return selectedContact.id || remoteUserId ? (
    <div className={className}>
      <div className="contact-details-header__expand-contact-view-icon">
        <ArrowStatefulIcon
          color="white"
          size={17}
          direction={collapsedContactView ? 'left' : 'right'}
          onClick={() => setCollapsedContactView(!collapsedContactView)}
        />
      </div>

      <ContactsDetailsHeader
        selectedContact={selectedContact}
        onChangeContactInfo={refreshSelectedContact}
        isLiveChatContacts
        deleteSelectedContact={() => setDeleteModalOpened(true)}
        collapsedContactView={collapsedContactView}
        isBusinessContact={isBusinessContact}
      />
      {selectedContact.loading ? (
        <Spinner
          size={24}
          show
          position="static"
          backgroundColor="transparent"
        />
      ) : (
        <div className="contact-details__blocks">
          {!collapsedContactView && (
            <LeadScoring leadScoring={selectedContact.leadScoring} />
          )}
          {!collapsedContactView && (
            <CustomerJourney
              customerJourney={selectedContact.customerJourney}
            />
          )}

          {!collapsedContactView && (
            <LinkedContacts
              linkedContacts={selectedContact?.linkedContacts}
              onContactClick={contact => updateRemoteUserId(contact.remoteId)}
            />
          )}

          <ChatVariablesTags
            contactId={selectedContact.id}
            tags={selectedContact.tags}
            selectedContact={selectedContact}
            onChange={refreshSelectedContact}
            isLiveChatContacts
            collapsedContactView={collapsedContactView}
          />

          <Sessions collapsedContactView={collapsedContactView} />
          <Notes
            contactId={selectedContact.id}
            notes={selectedContact.notes}
            selectedContact={selectedContact}
            onChange={refreshSelectedContact}
            collapsedContactView={collapsedContactView}
          />

          {!collapsedContactView && (
            <OTNTags contactId={selectedContact.id} otnTags={otnTags} />
          )}
          <ChatVariablesTab
            contactId={selectedContact.id}
            selectedContact={selectedContact}
            remoteUserId={selectedContact.remoteId}
            variables={displayVariables}
            onAttributeUpdated={refreshSelectedContact}
            isLiveChatContacts
            collapsedContactView={collapsedContactView}
            withHeaderButton
            withHeaderButtonText="Manage Attributes"
            headerButtonCss={{
              container: 'contact-details-header__manage-attributes-button',
            }}
            openArrowClassName="contact-details-header__arrow-icon-open"
            onHeaderButtonClick={onHeaderButtonClick}
            isBusinessContact={isBusinessContact}
          />
          <BusinessAttributes
            variables={displayVariables}
            isBusinessContact={isBusinessContact}
          />
          <div className="contact-details__footer-info">
            <div className="contact-details__footer-info-row">
              <UserOutlined style={{ color: '#a5bbd4' }} />
              <div>id: {selectedContact.recipientId}</div>
            </div>
            <div className="contact-details__footer-info-row">
              <ClockCircleFilled style={{ color: '#a5bbd4' }} />
              <div>
                Last seen:{' '}
                {moment(selectedContact.lastActive).format('YYYY-MM-DD HH:mm')}
              </div>
            </div>
          </div>
          <ContactDeleteModal
            showEraseRemoteMessagesOption={selectedContact.remoteId.startsWith(
              'em_',
            )}
            show={deleteModalOpened}
            emailAddress={selectedContact.email}
            onClose={() => setDeleteModalOpened(false)}
            onDelete={deleteContact}
          />
        </div>
      )}
    </div>
  ) : null;
};
